/**
 * TestFlight Diagnostics — temporary in-app instrumentation.
 *
 * Pure JS module that collects runtime signals about auth/session/network
 * health and exposes them to a diagnostics screen. All state is in-memory
 * + bounded ring buffers so it's safe to leave wired up.
 *
 * SAFE TO REMOVE for App Store submission:
 *   1. Delete this file.
 *   2. Delete src/components/DiagnosticsPanel.jsx.
 *   3. Delete src/pages/DiagnosticsPage.jsx.
 *   4. Remove the /diagnostics route from src/App.js.
 *   5. Remove the AccountPage Diagnostics row.
 *   6. Remove the `record*` callsites in App.js + ResumeContext.jsx.
 *
 * Strictly observational — does NOT change any business logic.
 * See ticket: emergent/testflight-diagnostics-0531.
 */

const MAX_API_ERRORS = 20;
const BRANCH_MARKER = 'refresh-token-persistence-fix-0531';

// In-memory state
const state = {
  branch: BRANCH_MARKER,

  // Resume Engine signals
  lastBackgroundedAt: null,        // ISO string
  lastResumeAt: null,              // ISO string
  lastResumeIdleMs: null,          // number
  lastResumeReason: null,          // string — fast-path / post-revalidate / etc.
  lastResumeEpoch: 0,              // last value bumped to
  lastTapEpoch: 0,                 // last value bumped to
  // Belt-and-braces: count of raw foreground events received, independent
  // of debounce / revalidation branching. If this is 0 after a resume,
  // Capacitor appStateChange is not firing at all. If it's > 0 but
  // lastResumeAt is null, the resume happened but bump path is broken.
  rawForegroundEvents: 0,
  lastRawForegroundAt: null,
  lastRawForegroundSource: null,   // 'capacitor:appStateChange' | 'visibilitychange'

  // Refresh token activity ring buffer + last-event scalars. Lets us
  // distinguish "token was never written" from "token was written but
  // persistence failed silently" from "token was cleared unexpectedly".
  refreshTokenWriteCount: 0,
  refreshTokenLastWriteAt: null,
  refreshTokenLastWriteSource: null,        // 'login' | 'refresh-rotation' | 'unknown'
  refreshTokenLastWriteOutcome: null,       // 'success' | 'cache-only-prefs-timeout' | 'cache-only-prefs-error' | 'cache-only-module-timeout' | 'verify-mismatch'
  refreshTokenLastWriteError: null,
  refreshTokenReadCount: 0,
  refreshTokenLastReadAt: null,
  refreshTokenLastReadSource: null,         // 'auth-gate-refresh' | 'resume-engine-revalidate' | 'diagnostics-probe' | 'boot-prewarm'
  refreshTokenLastReadResult: null,         // 'success' | 'missing' | 'null' | 'empty-string' | 'timeout' | 'error' | 'cache-hit' | 'cold-read-success'
  refreshTokenClearCount: 0,
  refreshTokenLastClearedAt: null,
  refreshTokenLastClearReason: null,        // 'logout' | 'force-local-logout' | 'set-empty-input' | 'persistence-verify-failed'
  refreshTokenCachePresent: 'unknown',      // 'true' | 'false' | 'not-hydrated' | 'unknown'
  refreshTokenPersistencePresent: 'unknown',
  // Session durability — set after every login/refresh-rotation that
  // attempts to write the refresh token. 'durable' means persistence
  // verified end-to-end (Preferences.set + read-back match). 'volatile'
  // means cache and/or localStorage mirror succeeded but Preferences did
  // not — the session works until the JS context dies, then it's gone.
  sessionDurability: 'unknown',             // 'durable' | 'volatile' | 'unknown'
  sessionDurabilityLastSetAt: null,
  sessionDurabilityLastReason: null,

  // Session Revalidation signals
  lastRevalidationAt: null,        // ISO string
  lastRevalidationOutcome: null,   // 'success' | 'network-fail' | 'auth-fail' | 'skipped'
  lastRevalidationErrorMessage: null,

  // Auth bootstrap signals
  cachedUserFallbackActive: false, // true if /auth/me failed and we fell back to localStorage user
  cachedUserFallbackReason: null,
  isRefreshingFlag: false,         // mirrors the global axios refresh lock

  // API failure ring buffer
  apiErrors: [],                   // [{at, method, url, status, code, message}]
};

// ─── Mutators (called from app code) ─────────────────────────────────────

export const recordBackgrounded = () => {
  state.lastBackgroundedAt = new Date().toISOString();
};

export const recordResume = ({ reason, idleMs, resumeEpoch }) => {
  state.lastResumeAt = new Date().toISOString();
  state.lastResumeReason = reason ?? null;
  state.lastResumeIdleMs = typeof idleMs === 'number' ? idleMs : null;
  if (typeof resumeEpoch === 'number') state.lastResumeEpoch = resumeEpoch;
};

// Belt-and-braces: called from the RAW Capacitor / visibility event handler,
// BEFORE any debounce / revalidation logic. Lets us see whether the OS
// even told us about the foreground. If this counter is 0 after a resume,
// Capacitor's appStateChange listener is broken; if > 0 but lastResumeAt
// is null, the bumpResumeEpoch path is the bug.
export const recordRawForegroundEvent = (source) => {
  state.rawForegroundEvents = (state.rawForegroundEvents || 0) + 1;
  state.lastRawForegroundAt = new Date().toISOString();
  state.lastRawForegroundSource = source || null;
};

// ── Refresh-token activity recorders ─────────────────────────────────────
// Pure observation — never throw. Called from nativeTokenStore.js write/
// read/clear paths so the diagnostics panel can show exactly what
// happened, when, and from which caller. Critical for distinguishing
// "token never written" from "token written but lost" from "token cleared
// by something unexpected" — see audit 2026-02-30.

export const recordRefreshTokenWrite = ({ source, outcome, error }) => {
  state.refreshTokenWriteCount = (state.refreshTokenWriteCount || 0) + 1;
  state.refreshTokenLastWriteAt = new Date().toISOString();
  state.refreshTokenLastWriteSource = source || 'unknown';
  state.refreshTokenLastWriteOutcome = outcome || null;
  state.refreshTokenLastWriteError = error ? String(error).slice(0, 240) : null;
};

export const recordRefreshTokenRead = ({ source, result }) => {
  state.refreshTokenReadCount = (state.refreshTokenReadCount || 0) + 1;
  state.refreshTokenLastReadAt = new Date().toISOString();
  state.refreshTokenLastReadSource = source || 'unknown';
  state.refreshTokenLastReadResult = result || null;
};

export const recordRefreshTokenClear = ({ reason }) => {
  state.refreshTokenClearCount = (state.refreshTokenClearCount || 0) + 1;
  state.refreshTokenLastClearedAt = new Date().toISOString();
  state.refreshTokenLastClearReason = reason || 'unknown';
};

export const recordRefreshTokenPresence = ({ cache, persistence }) => {
  if (cache !== undefined) state.refreshTokenCachePresent = cache;
  if (persistence !== undefined) state.refreshTokenPersistencePresent = persistence;
};

export const recordTapEpoch = (n) => {
  if (typeof n === 'number') state.lastTapEpoch = n;
};

export const recordRevalidation = ({ outcome, errorMessage }) => {
  state.lastRevalidationAt = new Date().toISOString();
  state.lastRevalidationOutcome = outcome ?? null;
  state.lastRevalidationErrorMessage = errorMessage ?? null;
};

// Records the outcome of a refresh-token persistence attempt on native
// (login + refresh-rotation paths in App.js). The corresponding state
// fields (sessionDurability, sessionDurabilityLastSetAt,
// sessionDurabilityLastReason) are already declared above and rendered
// by pages/DiagnosticsPage.jsx — this export wires the producer back to
// the consumer. Pure observation, never throws.
export const recordSessionDurability = ({ durability, reason }) => {
  state.sessionDurability = durability || 'unknown';
  state.sessionDurabilityLastSetAt = new Date().toISOString();
  state.sessionDurabilityLastReason = reason || null;
};

export const setCachedUserFallback = (active, reason = null) => {
  state.cachedUserFallbackActive = !!active;
  state.cachedUserFallbackReason = reason;
};

export const setIsRefreshingFlag = (flag) => {
  state.isRefreshingFlag = !!flag;
};

// Snapshot the Global Auth Gate state (sessionExpired flag + refresh
// in-flight + last failure code). Populated lazily by the diagnostics
// page via the optional getter passed in below.
let _authGateGetter = null;
export const setAuthGateGetter = (fn) => { _authGateGetter = typeof fn === 'function' ? fn : null; };

export const recordApiError = ({ method, url, status, code, message }) => {
  const entry = {
    at: new Date().toISOString(),
    method: method || '?',
    url: shortUrl(url),
    status: typeof status === 'number' ? status : null,
    code: code || null,
    message: (message || '').slice(0, 240),
  };
  state.apiErrors.unshift(entry);
  if (state.apiErrors.length > MAX_API_ERRORS) {
    state.apiErrors.length = MAX_API_ERRORS;
  }
};

// ─── Readers (consumed by DiagnosticsPanel) ──────────────────────────────

const shortUrl = (url) => {
  if (!url) return '';
  try {
    // Trim REACT_APP_BACKEND_URL prefix so the panel shows /api/... only.
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    return backend && url.startsWith(backend) ? url.slice(backend.length) : url;
  } catch {
    return url;
  }
};

/** Decode the JWT exp claim. Returns null on any failure. */
const decodeExp = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1]));
    if (!payload || typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
};

const readLocalStorage = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};

// Native refresh token probe state — populated by probeNativeRefreshToken()
// running in the background so the synchronous snapshot can read it
// without ever awaiting the Capacitor Preferences IPC (which can hang
// indefinitely on iOS in exactly the conditions this panel is meant to
// diagnose). Status values: 'pending' | 'web (n/a)' | 'true' | 'false' |
// 'read-failed' | 'module-load-failed' | 'timeout'.
let nativeProbeStatus = 'pending';
let nativeProbeStartedAt = null;

const NATIVE_PROBE_TIMEOUT_MS = 1500;

const _withTimeout = (promise, ms, timeoutValue = 'timeout') =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(timeoutValue), ms)),
  ]);

/**
 * Fire-and-forget background probe of the native refresh token. Idempotent
 * — re-running while a probe is in flight is a no-op. The result is read
 * synchronously by buildDiagnosticsSnapshot() so the panel never awaits.
 */
export const probeNativeRefreshToken = () => {
  if (nativeProbeStatus !== 'pending' && nativeProbeStatus !== 'timeout') return;
  if (nativeProbeStartedAt && Date.now() - nativeProbeStartedAt < NATIVE_PROBE_TIMEOUT_MS) return;
  nativeProbeStartedAt = Date.now();
  (async () => {
    try {
      const mod = await _withTimeout(import('./nativeTokenStore'), NATIVE_PROBE_TIMEOUT_MS, '__TIMEOUT__');
      if (mod === '__TIMEOUT__') { nativeProbeStatus = 'module-load-failed'; return; }
      const { isNative, getRefreshToken } = mod;
      if (!(isNative && isNative())) { nativeProbeStatus = 'web (n/a)'; return; }
      const result = await _withTimeout(
        (async () => {
          try {
            return (await getRefreshToken('diagnostics-probe')) ? 'true' : 'false';
          } catch { return 'read-failed'; }
        })(),
        NATIVE_PROBE_TIMEOUT_MS,
        'timeout'
      );
      nativeProbeStatus = result;
    } catch {
      nativeProbeStatus = 'module-load-failed';
    }
  })();
};

/**
 * Build the full diagnostics snapshot. PURELY SYNCHRONOUS. Never awaits.
 * The native refresh token field is sourced from a background probe so a
 * hung Capacitor IPC cannot freeze this call.
 */
export const buildDiagnosticsSnapshot = (extras = {}) => {
  const token = readLocalStorage('token');
  const userJson = readLocalStorage('user');
  const activeRole = readLocalStorage('activeRole');
  const impersonation = readLocalStorage('impersonation');

  let parsedUser = null;
  try { parsedUser = userJson ? JSON.parse(userJson) : null; } catch { parsedUser = null; }

  const tokenExpMs = decodeExp(token);
  const now = Date.now();
  const tokenExpiresInSeconds = tokenExpMs ? Math.round((tokenExpMs - now) / 1000) : null;

  // Kick off the native probe if it hasn't been done yet. Fire-and-forget;
  // we read whatever state is currently available, which on the first call
  // will be 'pending' and will update on subsequent renders.
  try { probeNativeRefreshToken(); } catch { /* noop */ }

  return {
    branch: state.branch,
    builtAt: process.env.REACT_APP_BUILD_TIME || 'unknown',
    nodeEnv: process.env.NODE_ENV || 'unknown',

    user: {
      email: parsedUser?.email || null,
      id: parsedUser?.user_id || parsedUser?.id || null,
      roles: parsedUser?.roles || (parsedUser?.role ? [parsedUser.role] : []),
      isAdmin: !!parsedUser?.is_admin,
      activeRole: activeRole || null,
      impersonationActive: !!impersonation,
    },

    tokens: {
      accessTokenPresent: !!token,
      accessTokenLooksValid: !!token && token.split('.').length === 3,
      accessTokenExpiresAt: tokenExpMs ? new Date(tokenExpMs).toISOString() : null,
      accessTokenExpiresInSeconds: tokenExpiresInSeconds,
      accessTokenExpired: tokenExpMs ? now > tokenExpMs : null,
      nativeRefreshTokenPresent: nativeProbeStatus,
    },

    resume: {
      lastBackgroundedAt: state.lastBackgroundedAt,
      lastResumeAt: state.lastResumeAt,
      lastResumeIdleMs: state.lastResumeIdleMs,
      lastResumeReason: state.lastResumeReason,
      lastResumeEpoch: state.lastResumeEpoch,
      lastTapEpoch: state.lastTapEpoch,
      rawForegroundEvents: state.rawForegroundEvents,
      lastRawForegroundAt: state.lastRawForegroundAt,
      lastRawForegroundSource: state.lastRawForegroundSource,
    },

    refreshToken: {
      writeCount: state.refreshTokenWriteCount,
      lastWriteAt: state.refreshTokenLastWriteAt,
      lastWriteSource: state.refreshTokenLastWriteSource,
      lastWriteOutcome: state.refreshTokenLastWriteOutcome,
      lastWriteError: state.refreshTokenLastWriteError,
      readCount: state.refreshTokenReadCount,
      lastReadAt: state.refreshTokenLastReadAt,
      lastReadSource: state.refreshTokenLastReadSource,
      lastReadResult: state.refreshTokenLastReadResult,
      clearCount: state.refreshTokenClearCount,
      lastClearedAt: state.refreshTokenLastClearedAt,
      lastClearReason: state.refreshTokenLastClearReason,
      cachePresent: state.refreshTokenCachePresent,
      persistencePresent: state.refreshTokenPersistencePresent,
      sessionDurability: state.sessionDurability,
      sessionDurabilityLastSetAt: state.sessionDurabilityLastSetAt,
      sessionDurabilityLastReason: state.sessionDurabilityLastReason,
    },

    revalidation: {
      lastRevalidationAt: state.lastRevalidationAt,
      lastRevalidationOutcome: state.lastRevalidationOutcome,
      lastRevalidationErrorMessage: state.lastRevalidationErrorMessage,
    },

    auth: {
      cachedUserFallbackActive: state.cachedUserFallbackActive,
      cachedUserFallbackReason: state.cachedUserFallbackReason,
      isRefreshingFlag: state.isRefreshingFlag,
      // Global Auth Gate state — populated lazily; null if gate isn't wired
      gate: (() => { try { return _authGateGetter ? _authGateGetter() : null; } catch { return null; } })(),
    },

    apiErrors: state.apiErrors.slice(0, MAX_API_ERRORS),

    snapshotTakenAt: new Date().toISOString(),
    ...extras,
  };
};

export const clearApiErrors = () => {
  state.apiErrors.length = 0;
};

// Convenience for the panel's "copy to clipboard" affordance.
export const snapshotToText = (snap) => {
  try { return JSON.stringify(snap, null, 2); } catch { return String(snap); }
};
